"use client";

import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Check, ChevronLeft, Search } from "lucide-react";
import { locales, getLocaleConfig, type Locale } from "@/config/locale";
import { cn } from "@/lib/utils";
import { FooterLocaleFlag } from "@/ui/components/footer-region-flags";

export interface LanguageDetailPanelProps {
	currentLocale: Locale;
	onBack: () => void;
	onSelect: (locale: Locale) => void;
}

export function LanguageDetailPanel({ currentLocale, onBack, onSelect }: LanguageDetailPanelProps) {
	const t = useTranslations("localeChannel");
	const [query, setQuery] = useState("");

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return [...locales];
		return locales.filter((locale) => {
			const config = getLocaleConfig(locale);
			return config.nativeName.toLowerCase().includes(q) || locale.toLowerCase().includes(q);
		});
	}, [query]);

	return (
		<div className="flex h-[36rem] min-h-0 flex-auto flex-col">
			<div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted-soft py-3 pl-4 pr-16">
				<button
					type="button"
					onClick={onBack}
					aria-label={t("back")}
					className="-ml-1 inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<ChevronLeft className="h-4 w-4" />
				</button>
				<h3 className="text-sm font-semibold text-foreground">{t("languageLabel")}</h3>
			</div>

			<div className="shrink-0 px-4 py-3">
				<div className="flex min-h-11 items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring">
					<Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
					<input
						type="search"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={t("searchLanguagePlaceholder")}
						className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 md:text-sm"
						aria-label={t("searchLanguagePlaceholder")}
					/>
				</div>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4 [scrollbar-color:var(--muted-foreground)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
				{filtered.length === 0 ? (
					<p className="px-2 py-6 text-center text-xs text-muted-foreground">{t("noResults")}</p>
				) : (
					filtered.map((locale) => {
						const config = getLocaleConfig(locale);
						const isActive = locale === currentLocale;
						return (
							<button
								key={locale}
								type="button"
								aria-current={isActive ? "true" : undefined}
								onClick={() => onSelect(locale)}
								className={cn(
									"flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
									isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted/50 text-foreground",
								)}
							>
								<FooterLocaleFlag locale={locale} className="h-4 w-6 shrink-0 rounded-[2px]" />
								<span className="min-w-0 flex-1 truncate font-medium">{config.nativeName}</span>
								{isActive ? <Check className="h-4 w-4 shrink-0" aria-hidden /> : null}
							</button>
						);
					})
				)}
			</div>
		</div>
	);
}
